<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'

import { destinationAddress, destinationCompany, shipmentTypes } from '@/utils/delivery-management'
import { MinimalShipment, type AdrInformation } from '@/types/delivery-management'
import { splitAddressString } from '@/utils/string'
import { MinimalResource } from '@/types/general'
import { numberFormatter } from '@/utils/numbers'
import { image } from '@/utils/assets'

import MyLabeledField from '@/components/my-components/MyLabeledField.vue'
import ShipmentTemperatureIcon from '@/components/delivery-management/ShipmentTemperatureIcon.vue'
import ShipmentModal from '@/components/delivery-management/ShipmentModal.vue'

interface Props {
    shipment: MinimalShipment
    linkedTour?: MinimalResource
}

const props = defineProps<Props>()

const { t } = useI18n()

const shipmentModalOpen = ref(false)

const address = computed(() => {
    return splitAddressString(destinationAddress(props.shipment))
})

const itemCount = computed(() => {
    return props.shipment.totalColli ?? props.shipment.collisCount ?? 0
})

const totalWeight = computed(() => {
    if (props.shipment.totalWeight == null) return '—'
    return numberFormatter.format(props.shipment.totalWeight)
})

const totalVolume = computed(() => {
    if (props.shipment.totalVolume == null) return '—'
    return numberFormatter.format(props.shipment.totalVolume)
})

const adrInformationList = computed<AdrInformation[]>(() => {
    if (!props.shipment.collis) return []

    const uniqueAdr = new Map<string, AdrInformation>()
    for (const colli of props.shipment.collis) {
        if (colli.adrInformation) {
            uniqueAdr.set(colli.adrInformation.unNumber, colli.adrInformation)
        }
    }
    return Array.from(uniqueAdr.values())
})

function formatAdrString(adr: AdrInformation): string {
    return `${adr.unNumber}: ${adr.name} class ${adr.class}`
}

function showShipment() {
    shipmentModalOpen.value = true
}
</script>
<template>
    <div
        class="relative flex rounded-xl space-x-2 bg-white p-3 shadow-md dark:bg-dark-400 [&.sortable-chosen.drag-ghost-card]:max-w-[300px]"
        :class="{ 'opacity-70': linkedTour }"
    >
        <div class="flex flex-col justify-between">
            <mdi:drag class="handle cursor-grab text-xl text-dark-700 transition dark:text-white" />

            <mdi:eye
                class="cursor-pointer hover:text-primary-400 dark:hover:text-primary-300 min-h-5 min-w-5"
                @click="showShipment"
            />
        </div>

        <div class="flex flex-col max-w-48 !mr-auto">
            <span
                v-if="linkedTour"
                class="ml-2 max-w-[110px] inline-flex items-center rounded-full px-2 py-[3px] truncate text-[11px] font-medium bg-blue-100 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:ring-blue-800"
                v-text="linkedTour.name"
            />
            <span class="flex text-sm dark:text-primary-50 break-all">
                <span v-truncate-tooltip class="truncate">
                    {{ shipment.shipmentNumber ?? shipment.name ?? '-' }}
                    -
                    {{ t(shipmentTypes[shipment.type]).toLowerCase() }}
                </span>
            </span>

            <span
                class="text-sm font-semibold text-blue-500 dark:text-primary-50"
                v-text="destinationCompany(shipment)"
            />

            <span
                v-truncate-tooltip="address.address"
                class="text-xs truncate"
                v-text="address.address"
            />
            <span
                v-truncate-tooltip="address.zipcode + ' ' + address.city"
                class="text-xs"
                v-text="address.zipcode + ' ' + address.city"
            />
        </div>

        <div class="flex flex-col flex-shrink-0">
            <MyLabeledField :title="t('itemCount')" :content="itemCount.toString()" />
            <MyLabeledField :title="t('totalWeight')" :content="totalWeight + ' kg'" />
        </div>
        <div class="flex flex-col flex-shrink-0">
            <MyLabeledField :title="t('totalCbm')" :content="totalVolume + ' m³'" />
            <MyLabeledField :title="t('totalLdm')" content="-" />
        </div>

        <div class="flex flex-col items-center space-y-1">
            <ShipmentTemperatureIcon
                v-if="shipment.temperatureRequirement !== null"
                :requirement="shipment.temperatureRequirement"
                icon-only
            />

            <div v-if="adrInformationList.length > 0" class="flex flex-wrap gap-1">
                <template v-for="adr in adrInformationList" :key="adr.unNumber">
                    <img
                        v-for="labelImage in adr.labelImages"
                        :key="labelImage"
                        v-tooltip="formatAdrString(adr)"
                        class="w-[30px] h-[30px]"
                        :src="image(labelImage)"
                        :alt="adr.class"
                    />
                </template>
            </div>
        </div>

        <ShipmentModal
            v-if="shipmentModalOpen"
            :id="shipment.id"
            v-model:open="shipmentModalOpen"
        />
    </div>
</template>
